import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router";
import { useSelector } from "react-redux";
import { debounce } from "lodash";
import { Typography, TextField, InputAdornment, IconButton, Container } from "@mui/material";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import CloseIcon from '@mui/icons-material/Close';

import { apiEndpoints } from "../../config";
import { getOnlineNode } from "../../core/getNode";
import { alertMessages } from "../../partials/alertMessages";
import SearchItem from "./searchItem";
import Pagination from "../../partials/pagination";
import IframeDialog from "../../partials/iframeDialog";
import { serverUrl } from "../../config";

import PageHeader from "../../partials/pageHeader";
import { Row, Col } from "react-bootstrap";
import CompanyappLoader from "../../partials/companyappLoader";
import Error from "../../partials/error";

const pageSize = 20;

export default function SearchScreen(props) {
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [dataLength, setDataLength] = useState(0);
  const [data, setData] = useState([]);
  const [activeForm, setActiveForm] = useState(null);
  const [showIframe, setShowIframe] = useState(false);
  const [search, setSearch] = useState('');
  const [initial, setInitial] = useState(true);

  const user = useSelector((state) => state).authReducer.user;
  const params = useParams();

  const debouncedSearch = useCallback(
    debounce((query) => {
      setCurrentPage(1);
      setIsLoading(true);
      getContent(1, query);
      setInitial(false);
    }, 700)
  );

  useEffect(() => {
    if (search !== '') {
      debouncedSearch(search);
    } else {
      setInitial(true);
      setData([]);
    }
    return () => debouncedSearch.cancel();
    // eslint-disable-next-line
  }, [search]);

  const _setError = (_isError, _errorStatus, _errorMessage) => {
    setIsLoading(false);

    setIsError(_isError);
    setErrorStatus(_errorStatus);
    setErrorMessage(_errorMessage);
  };

  const processData = (_data) => {
    let tempData = {}
    if (_data.length > 0) {
      _data.forEach((e) => {
        if (tempData[e.type]) {
          tempData[e.type].push(e);
        }
        else {
          tempData[e.type] = [e];
        }
      })
    }
    setData(tempData);
  }

  const getContent = (_currentPage, query) => {
    let path = `${apiEndpoints.search}?title=${query || search}&items_per_page=${pageSize}&page=${_currentPage - 1}&_format=json`;
    getOnlineNode(path, user.access_token)
      .then((response) => {
        setDataLength(response.data.rows.length);
        processData(response.data.rows);
        setTotalItems(parseInt(response.data.pager.total_items, 10));
        setIsLoading(false);
      })
      .catch((_error) => {
        if (_error.response) {
          //console.log('@rest response: ', _error.response);
          _setError(true, _error.response.status, _error.response.statusText);
        } else if (_error.request) {
          //console.log('@rest request: ', _error.request);
          _setError(true, 0, alertMessages.requestError.message);
        } else {
          //console.log('@rest unknown: ', _error);
          _setError(true, 0, alertMessages.unkownError.message);
        }
      });
  }

  const renderGroupHeading = (type, index) => {
    let heading = ""
    switch (type) {
      case "News":
        heading = "News";
        break;
      case "Event":
      case "Events":
        heading = "Events";
        break;
      case "Blogs":
        heading = "Blogs";
        break;
      case "Video":
      case "Videos":
        heading = "Videos";
        break;
      case "Podcast":
      case "Podcasts":
        heading = "Podcasts";
        break;
      case "Gallery":
        heading = "Galleries";
        break;
      case "Document":
        heading = "Documents";
        break;
      case "Course":
        heading = "Training Courses";
        break;
      case "Quiz":
        heading = "Tests & Quizzes";
        break;
      case "Documents":
        heading = "Documents";
        break;
      case "Link":
      case "External Resource":
        heading = "Useful Links";
        break;
      case "Company Directory":
        heading = "Company Directory";
        break;
      case "Webform":
        heading = "Surveys & Forms";
        break;
      default:
        heading = `Undefined ${type}`;
        break;
    }

    return (
      <div key={`group-${index}`}>
        <Row className="mt-4 mb-2">
          <Col xs={12}>
            <Typography
              owner="client"
              variant="h5"
              color="clientPrimary.main"
            >
              {heading}
            </Typography>
          </Col>
        </Row>
        {data[type].map((e, index) => {
          return (
            <Row key={`row-${index}`}>
              <Col xs={12} className="ps-2 ps-md-5">
                <SearchItem
                  item={e}
                  showForm={(e) => {
                    setActiveForm(e);
                    setShowIframe(true);
                  }}
                />
              </Col>
            </Row>
          )
        })}
      </div>
    )
  }

  /**
   * @function onPaginate
   * @description Pagination callback
   * @param {int} _index - Page number
   */
  const onPaginate = (_index) => {
    setIsLoading(true);
    setCurrentPage(_index);
    getContent(_index);
  };


  // Pagination component
  const renderPagination = () => {
    if (totalItems <= pageSize) {
      return null;
    } else {
      return (
        <Pagination
          owner="client"
          containerClasses={`creator-pagination d-flex justify-content-center align-items-center mt-4`}
          previousDisabled={currentPage === 1 ? true : false}
          nextDisabled={dataLength < pageSize || (totalItems % (currentPage * pageSize)) === 0 ? true : false}
          currentPage={currentPage}
          totalItems={totalItems}
          onPrevious={() => {
            onPaginate(currentPage - 1);
          }}
          onNext={() => {
            onPaginate(currentPage + 1);
          }}
        />
      );
    }
  };

  return (
    <>
      {isError ? (
        <Error status={errorStatus} message={errorMessage} />
      ) : (
        <main className="search screen">
          <PageHeader
            pageName="Search"
            filters={false}
            owner="client"
          />
          <Container className="px-0 mt-4 pt-3">
            <Row className="pe-0 d-flex justify-content-center">
              <Col xs={12} sm={6} md={6} lg={4} xl={5} className="px-xl-5">
                <TextField
                  placeholder="Search the app"
                  className="w-100"
                  sub_variant="search"
                  owner="client"
                  autoFocus={true}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && search.trim() !== "") {
                      e.preventDefault();
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <>
                        <InputAdornment position="start">
                          <IconButton
                            className="navbar-btn p-0"
                            color="clientPrimary"
                            onClick={() => {
                              setSearch('');
                            }}
                          >
                            {search !== ''
                              ?
                              <CloseIcon sx={{ fontSize: "1.2rem" }} />
                              :
                              <SearchIcon sx={{ fontSize: "1.2rem" }} />
                            }
                          </IconButton>
                        </InputAdornment>
                      </>
                    ),
                  }}
                />
              </Col>
            </Row>
          </Container>
          <Container className="px-0 mt-3">
            <Row className="pe-0 d-flex justify-content-center">
              {isLoading ?
                <Col>
                  <CompanyappLoader />
                </Col> :
                Object.keys(data).length > 0 ?
                  <>
                    <Col md={10} className="mt-4">
                      {Object.keys(data).map((e, index) => renderGroupHeading(e, index))}
                      {renderPagination()}
                      <IframeDialog
                        open={showIframe}
                        onClose={() => {
                          setActiveForm(null)
                          setShowIframe(false);
                        }}
                        title={activeForm?.title}
                        src={`${serverUrl}/node/${activeForm?.id}?app=true`}
                      />
                    </Col>
                  </>
                  :
                  <Col xs={12} md={6} className="d-flex align-items-center justify-content-center mt-2">
                    <Typography
                      className="text-center"
                      owner="client"
                      variant="h6"
                      fontWeight="light"
                    >
                      {initial ? 'Start a search by typing something' : 'No results found'}
                    </Typography>
                  </Col>
              }
            </Row>
          </Container>
        </main>
      )}
    </>
  )
}